import { useState, useRef, useEffect, ReactNode } from 'react';
import styles from './WheelOfFortune.module.scss';
import { Wheel } from './Wheel/Wheel';
import { WheelPrize } from './WhelePrize/WheelPrize';
import { GameBalances, WheelGameFrozenWallet } from './GameBalances/GameBalances';
import { Button } from '../../components/Buttons/Button/Button';
import { WheelOfFortuneModeSwitcher } from './WheelOfFortuneModeSwitcher/WheelOfFortuneModeSwitcher';
import { useDispatch, useSelector, useStore } from '../../hooks';
import { wheelOfFortuneGetInfo } from '../../redux/games/wheel-of-fortune/get-info.handler';
import { Loader } from '../../components/Loader/Loader';
import { ErrorSnackbar } from '../../components/ErrorSnackbar/ErrorSnackbar';
import currencyWhiteIcon from '../../icons/currency-white.png';
import { formatNumberWithSpaces } from '../../utils/numbers';
import { WheelOfFortuneMode } from '../../redux/games/wheel-of-fortune';
import { InfoSnackbar } from '../../components/InfoSnackbar/InfoSnackbar';
import { wheelOfFortuneSpin } from '../../redux/games/wheel-of-fortune/spin.handler';
import { Modal } from '../../components/Modal/Modal';
import { TicketType } from '../../components/TicketsPanel/Ticket';
import prizeIcon from '../../components/Modal/ModalBonus/icons/bonus.png'
import { WheelWinnerPrize } from './WhileWinnerPrize/WheelWinnerPrize';
import { FreeSpinsBlock } from './FreeSpinsBlock/FreeSpinsBlock';
import { NotEnoughMoney } from '../../components/Popup/NotEnoughMoney/NotEnoughMoney';
import { BalanceErrorType, createBalanceErrorMessage } from '../../hooks/error';

export interface IWheelOffurtunePrize {
    node: ReactNode;
    type: TicketType | 'balance',
    value: number;
}

interface ISector {
    hue: number;
    prize: IWheelOffurtunePrize;
    angle: number;
    sectorAngle: number;
}

export const WheelOfFortune = () => {
    const [winnerSector, setWinnerSector] = useState<number>(0);
    const [isSpinning, setIsSpinning] = useState<boolean>(false);
    const [spinWalletSnapshot, setSpinWalletSnapshot] = useState<WheelGameFrozenWallet | null>(null);
    const [infoMessage, setInfoMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNotEnoughMoneyOpen, setIsNotEnoughMoneyOpen] = useState(false);
    const [sectors, setSectors] = useState<ISector[]>([]);

    const currentMode = useSelector(store => store.games.wheelOfFortune.mode);

    const { costs, wheel, freeSpinsCount, loading, error } = useSelector(store => store.games.wheelOfFortune);
    const balance = useSelector(store => store.user.info.balance);

    const dispatch = useDispatch();
    const store = useStore();
    const wheelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!wheel)
            return;

        const prizes = wheel[currentMode];
        if (!prizes?.length)
            return;

        const sectorCount = prizes.length;
        const sectorAngle = 360 / sectorCount;
        const bySlot: Array<(typeof prizes)[number] | undefined> = Array.from({ length: sectorCount });

        for (const p of prizes) {
            if (p.sectorIndex >= 0 && p.sectorIndex < sectorCount)
                bySlot[p.sectorIndex] = p;
        }

        const sectors = bySlot.map((prize, i) => ({
            hue: 200 + 3 * 5,
            prize: prize
                ? {
                    node: <WheelPrize type={prize.prizeType} value={prize.prizeValue} />,
                    type: prize.prizeType,
                    value: prize.prizeValue,
                }
                : {
                    node: <WheelPrize type="balance" value={0} />,
                    type: 'balance' as const,
                    value: 0,
                },
            angle: i * sectorAngle,
            sectorAngle,
        }));

        setSectors(sectors);
    }, [wheel, currentMode]);

    const handleSpin = async () => {
        if (freeSpinsCount < 1 && currentMode === WheelOfFortuneMode.Free) {
            setInfoMessage('Бесплатные попытки появятся позже. А пока используйте другой режим.');
            return;
        }
        
        const cost = costs?.[currentMode] ?? 0;
        if (cost > 0 && balance < cost) {
            setInfoMessage('');
            setIsNotEnoughMoneyOpen(true);
            return;
        }

        if (isSpinning)
            return;

        if (spinWalletSnapshot !== null)
            return;

        const snap: WheelGameFrozenWallet = (() => {
            const s = store.getState();
            return {
                balance: s.user.info.balance,
                tickets: { ...s.user.info.tickets },
            };
        })();

        setSpinWalletSnapshot(snap);

        const resultAction = await dispatch(wheelOfFortuneSpin(currentMode));
        if (wheelOfFortuneSpin.fulfilled.match(resultAction)) {
            const { sectorIndex } = resultAction.payload;
            if (sectorIndex !== undefined) {
                setIsSpinning(true);
                setWinnerSector(sectorIndex);
            }
            else
                setSpinWalletSnapshot(null);
        }
        else
            setSpinWalletSnapshot(null);
    };

    const handleAnimationEnd = () => {
        setSpinWalletSnapshot(null);
        setIsSpinning(false);
        setIsModalOpen(true);
    };

    const getButtonCaption = () => {
        if (!costs)
            return '';

        return costs[currentMode]
            ? <span className={styles.buyPrice}>
                    {`Крутить за `}
                    <img src={currencyWhiteIcon} alt="Currency" />
                    {' '}
                    { formatNumberWithSpaces(costs[currentMode]) }
               </span>
            : 'Крутить бесплатно';
    };

    useEffect(() => {
        dispatch(wheelOfFortuneGetInfo());
    }, [dispatch]);

    const showPrize = (prize: IWheelOffurtunePrize) => <WheelWinnerPrize type={prize.type} value={prize.value} />;

    return (
        <div className={styles.container}>
            <div className={styles.topBlock}>
                <WheelOfFortuneModeSwitcher isSpinning={ isSpinning } />
                <GameBalances freezeWallet={spinWalletSnapshot} />
            </div>

            <div className={ styles.splitter } />

            <Wheel
                ref={wheelRef}
                sectors={sectors}
                winnerIndex={winnerSector}
                isSpinning={isSpinning}
                onAnimationEnd={handleAnimationEnd}
            />

            <FreeSpinsBlock />
            <Button
                className={styles.button}
                caption={ getButtonCaption() }
                handler={ handleSpin }
                disabled={
                    loading ||
                    isSpinning ||
                    spinWalletSnapshot !== null
                }
            />

            { isModalOpen &&
                <Modal
                    name='WheelOfFortune'
                    title='Вы выиграли'
                    message={ showPrize(sectors[winnerSector].prize) }
                    icon={ prizeIcon }
                    button={ <Button caption='Закрыть' type='orange' handler={ () => setIsModalOpen(false) } /> }
                />
            }

            { isNotEnoughMoneyOpen && (
                <NotEnoughMoney
                    textNode={ createBalanceErrorMessage(costs?.[currentMode] ?? 0, BalanceErrorType.WheelOfFortunePaidSpin) }
                    onClose={ () => setIsNotEnoughMoneyOpen(false) }
                />
            ) }

            { loading && <Loader noBackground={true} /> }
            { error.length > 0 && <ErrorSnackbar errorMessage={ error } /> }
            {infoMessage.length > 0 && <InfoSnackbar infoMessage={infoMessage} onCloseHandler={() => setInfoMessage('')} />}
        </div>
    );
};